import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { paneImageOf } from '~/engine/view/paneImage';
import { PlaneView } from '../../view/views/PlaneView';
import { TextureView } from '../../view/TextureView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { ToolAction } from '../ToolAction';

/**
 * How far (voxels) the coordinate `voxel` lies beyond the volume's extent
 * `[0, size]` along one axis, 0 within it.
 */
function distanceOutside(voxel: number, size: number): number {
    if (voxel < 0) {
        return -voxel;
    }
    return voxel > size ? voxel - size : 0;
}

/**
 * {@link ScrollAction} navigates through image slices
 * along the viewing direction of a {@link PlaneView}: the slices of the
 * image the view shows ({@link paneImageOf}), which for a view that draws
 * a fused secondary alone are the secondary's, not the primary's. A view
 * that shows no image does not scroll.
 *
 * This is a global action: always available regardless
 * of which tool is active.
 */
export class ScrollAction extends ToolAction {
    /**
     * Scroll one slice in the given direction.
     * @param view The PlaneView to scroll
     * @param delta  1 = backward/up the stack (PageUp / scroll-up),
     *              -1 = forward/down the stack  (PageDown / scroll-down)
     * @returns true if the slice was changed
     */
    private scrollSlice(view: PlaneView, delta: 1 | -1): boolean {
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            return false;
        }
        const direction =
            delta < 0
                ? view.viewDirection
                : vec3.negate(vec3.create(), view.viewDirection);
        // The shown image's grid is in the pane's frame, so its step and
        // its extent apply to the shared point with no mapping.
        const step = pane.image.calculateStep(
            view.planeIntersectionPoint.coordinates,
            direction
        );
        const newCoords = vec3.add(
            vec3.create(),
            view.planeIntersectionPoint.coordinates,
            step
        );
        // Never step the shared point further outside the volume along the
        // scroll axis: readouts would report slices that do not exist. A
        // point already outside (a fused image shown alone need not cover
        // the primary's point) may step back toward the volume, one slice at
        // a time. Only that axis is gated: pan and cursor zoom can park the
        // shared point outside the volume in-plane, and full-containment
        // gating would then refuse every scroll until the point is dragged
        // back over the image.
        const grid = pane.grid;
        const voxelBefore = grid.toVoxelSpace(
            view.planeIntersectionPoint.coordinates
        );
        const voxelAfter = grid.toVoxelSpace(newCoords);
        const move = vec3.subtract(vec3.create(), voxelAfter, voxelBefore);
        let axis: 0 | 1 | 2 = 0;
        if (Math.abs(move[1]) > Math.abs(move[axis])) {
            axis = 1;
        }
        if (Math.abs(move[2]) > Math.abs(move[axis])) {
            axis = 2;
        }
        const axisSize = [grid.sizeX, grid.sizeY, grid.sizeZ][axis];
        const sliceAfter = Math.floor(voxelAfter[axis]);
        const insideAfter = sliceAfter >= 0 && sliceAfter < axisSize;
        if (
            !insideAfter &&
            distanceOutside(voxelAfter[axis], axisSize) >=
                distanceOutside(voxelBefore[axis], axisSize)
        ) {
            return false;
        }
        view.updatePlaneIntersectionPoint(newCoords);
        return true;
    }

    public onMouseWheel(
        delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.scrollSlice(view, delta)) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * The slice step a key asks for over a view: 1 for PageUp and ArrowUp,
     * -1 for PageDown and ArrowDown, or null when the key is not a slice key
     * or the view is not a {@link PlaneView} that shows an image.
     */
    private static sliceDelta(key: string, view: TextureView): 1 | -1 | null {
        if (!(view instanceof PlaneView) || paneImageOf(view).kind === 'none') {
            return null;
        }
        if (key === 'PageUp' || key === 'ArrowUp') {
            return 1;
        }
        if (key === 'PageDown' || key === 'ArrowDown') {
            return -1;
        }
        return null;
    }

    /**
     * Whether `key` over `view` is a slice key over an image: the keys
     * {@link onKeyPress} consumes, whether or not the slice can move.
     */
    public ownsKey(key: string, view: TextureView): boolean {
        return ScrollAction.sliceDelta(key, view) !== null;
    }

    public onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        view: TextureView
    ): EventHandlerResult {
        const delta = ScrollAction.sliceDelta(key, view);
        if (delta === null || !(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        // Consumed also at the end of the stack, where the slice does not
        // move: the key belongs to the image under the pointer, and passed
        // on it would move the keyboard focus to the next tile instead.
        this.scrollSlice(view, delta);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
